// src/features/chat/panel/ChatLightbox.tsx
// Full-overlay image gallery for the chat panel: every image attachment of
// the loaded conversation, keyboard-navigable, with a download action.

import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { ghostIconBtn } from './panelShared';

export interface LightboxImage {
  id: string;
  name: string;
}

export function ChatLightbox({
  images,
  index,
  resolveUrl,
  onClose,
  onNavigate,
}: {
  images: LightboxImage[];
  index: number;
  /** Resolve (and cache) the full-size download URL for an attachment id. */
  resolveUrl: (attachmentId: string) => Promise<string>;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const { t } = useI18n();
  const image = images[index];
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!image) return;
    let cancelled = false;
    setLoading(true);
    setUrl('');
    resolveUrl(image.id)
      .then((u) => { if (!cancelled) { setUrl(u); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [image?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = useCallback((delta: number) => {
    if (images.length === 0) return;
    onNavigate((index + delta + images.length) % images.length);
  }, [index, images.length, onNavigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  if (!image) return null;

  const download = async () => {
    try {
      const u = url || (await resolveUrl(image.id));
      const a = document.createElement('a');
      a.href = u;
      a.download = image.name;
      a.click();
    } catch {
      // silent
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={image.name}
    >
      {/* Backdrop — aria-hidden like the rail's drawer backdrop; Esc and the
          header close button are the keyboard paths. */}
      <div
        className="absolute inset-0 bg-background/90 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Header: counter + actions */}
      <div className="relative flex items-center gap-2 px-4 py-3">
        <span className="text-xs tabular-nums text-muted-foreground">
          {t('chat.entityPanel.imageCounter', { current: index + 1, total: images.length })}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{image.name}</span>
        <button
          type="button"
          onClick={() => void download()}
          className={ghostIconBtn}
          aria-label={t('chat.entityPanel.download')}
          title={t('chat.entityPanel.download')}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onClose}
          className={ghostIconBtn}
          aria-label={t('chat.entityPanel.closeLightbox')}
          title={t('chat.entityPanel.closeLightbox')}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Image — the container is click-through so empty space lands on the
          backdrop and closes; the image and the arrows stay interactive. */}
      <div className="pointer-events-none relative flex min-h-0 flex-1 items-center justify-center px-12 pb-6">
        {loading ? (
          <div className="h-40 w-60 animate-pulse rounded-lg bg-muted" />
        ) : url ? (
          <img
            src={url}
            alt={image.name}
            className="pointer-events-auto max-h-full max-w-full rounded-lg object-contain"
          />
        ) : (
          <p className="text-sm text-muted-foreground">{t('chat.entityPanel.loadError')}</p>
        )}

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => step(-1)}
              className={`${ghostIconBtn} pointer-events-auto absolute left-3 top-1/2 -translate-y-1/2 bg-background/60`}
              aria-label={t('chat.entityPanel.previousImage')}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              className={`${ghostIconBtn} pointer-events-auto absolute right-3 top-1/2 -translate-y-1/2 bg-background/60`}
              aria-label={t('chat.entityPanel.nextImage')}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
